import React, { useState, useRef, useEffect } from 'react';

interface CarouselItem {
  id: string;
  imageUrl: string;
  title: string;
  alt?: string;
}

interface ImageCarouselProps {
  items: CarouselItem[];
  className?: string;
  maxHeight?: string;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ 
  items, 
  className = '',
  maxHeight = '60vh'
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  // Handle scrolling through the carousel
  const handleScroll = () => {
    if (!carouselRef.current) return;
    
    const scrollPosition = carouselRef.current.scrollLeft;
    const itemWidth = carouselRef.current.offsetWidth;
    
    // Calculate which item is most visible
    const newIndex = Math.round(scrollPosition / itemWidth);
    
    if (newIndex !== activeIndex && newIndex >= 0 && newIndex < items.length) {
      setActiveIndex(newIndex);
    }
  };
  
  // Scroll to selected indicator
  const scrollToIndex = (index: number) => {
    if (!carouselRef.current) return;
    
    const itemWidth = carouselRef.current.offsetWidth;
    carouselRef.current.scrollTo({
      left: index * itemWidth,
      behavior: 'smooth'
    });
    
    setActiveIndex(index);
  };
  
  // Handle touch events for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    
    if (distance > minSwipeDistance && activeIndex < items.length - 1) {
      // Swipe left
      scrollToIndex(activeIndex + 1);
    } else if (distance < -minSwipeDistance && activeIndex > 0) {
      // Swipe right
      scrollToIndex(activeIndex - 1);
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  // Set up scroll event listener
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    
    // Use throttled scroll event for better performance
    let scrollTimeout: NodeJS.Timeout;
    const handleScrollThrottled = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 100);
    };
    
    carousel.addEventListener('scroll', handleScrollThrottled);
    
    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      carousel.removeEventListener('scroll', handleScrollThrottled);
    };
  }, [activeIndex]);
  
  // Initialize the carousel to show the first item
  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = 0;
    }
  }, []);
  
  return (
    <div className={`${className} w-full flex flex-col items-center`}>
      {/* Main carousel scroll container */}
      <div 
        ref={carouselRef}
        className="flex w-full overflow-x-auto scrollbar-hide snap-x snap-mandatory" 
        style={{ 
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none' // IE/Edge
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Add a spacer div before the first item to enable centering */}
        <div className="flex-none w-[10%] md:w-[10%]" aria-hidden="true" />
        
        {items.map((item, index) => (
          <div 
            key={item.id} 
            className="flex-none w-[80%] flex flex-col items-center snap-center px-[2%] mx-[3%]"
          >
            <h3 className="text-lg sm:text-xl font-bold mb-3 text-center">{item.title}</h3>
            <div className="rounded-lg overflow-hidden bg-transparent mb-3 w-full flex justify-center">
              <div 
                className="aspect-[3/4] relative bg-transparent rounded-md overflow-hidden mx-auto" 
                style={{ maxHeight }}
              >
                <img
                  src={item.imageUrl}
                  alt={item.alt || item.title}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        ))}
        
        {/* Add a spacer div after the last item to enable centering */}
        <div className="flex-none w-[10%] md:w-[10%]" aria-hidden="true" />
      </div>
      
      {/* Indicators */}
      <div className="flex justify-center gap-3 mt-4">
        {items.map((item, index) => (
          <button
            key={`indicator-${item.id}`}
            className={`w-3 h-3 transition-all ${
              index === activeIndex ? 'bg-black' : 'bg-gray-300'
            }`}
            onClick={() => scrollToIndex(index)}
            aria-label={`Go to ${item.title}`}
          />
        ))}
      </div>
    </div>
  );
};

// Add scrollbar hiding styles for Webkit browsers
const style = document.createElement('style');
style.textContent = `
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;
document.head.appendChild(style);

export default ImageCarousel; 